import React from "react";
import { Helmet } from "react-helmet";
import { ShareSection } from "../../components/template";

// Hashtag section
const HashtagSection = ({ tags }) => (
  <div className="mt-6 flex flex-wrap md:gap-3 gap-1 justify-center md:mb-6 mb-3">
    {tags.map((tag, index) => (
      <span
        key={index}
        className="bg-red-700 bg-opacity-10 text-red-700 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all duration-300 m-1 mb-2 hover:bg-red-700 hover:text-white"
      >
        {tag}
      </span>
    ))}
  </div>
);

const Blog12 = () => {
  const hashtags = [
    "#morninghabits",
    "#mindfulmornings",
    "#innerpeace",
    "#selfcare",
    "#ParamLife",
  ];

  return (
    <>
      <Helmet>
        <title>9 Morning Habits That Power Up Your Whole Day</title>
        <meta
          name="description"
          content="9 gentle morning habits that help you start your day with peace, clarity, and grounded energy."
        />
      </Helmet>

      <article className="max-w-4xl mx-auto mt-10 px-6 py-4 leading-relaxed text-gray-800">
        <h1 className="text-secondary text-left text-3xl font-semibold mb-8">
          ☀ 9 Morning Habits That Power Up Your Whole Day
        </h1>
   <img
          src="/assets/knowyourself/blogs/blog12.png"
          alt="7 Things Our Grandparents Did Right (But We Ruined)"
          className="w-[80%] max-w-xl rounded-2xl shadow-md mx-auto mb-10"
        />

        <section className="space-y-4 mb-8">
          <p>Let me start with a confession.</p>

          <p>
            For a long time, my mornings were chaotic.
            <br />
            I’d wake up already tired, grab my phone, scroll through other
            people’s lives, rush through my routine, skip breakfast, and wonder
            why I felt disconnected before 9 a.m.
          </p>

          <p>If that sounds familiar, you’re not alone.</p>

          <p>
            Most of us don’t realize how much the first hour of our day shapes
            everything that follows. We wake up reactive — not intentional. We
            begin our days answering the world instead of listening to
            ourselves.
          </p>

          <p className="text-[#578d48] font-semibold">
            But when you start your morning with peace, clarity, and grounded
            energy — it changes everything.
          </p>

          <p>
            You show up calmer. Kinder. More focused. More you.
          </p>

          <p>
            And no, this isn’t about waking up at 5 a.m. or having a perfect
            “Instagram” routine.
            <br />
            This is about slow, honest mornings that feel good to your soul.
          </p>

          <p className="text-[#578d48] font-semibold">
            Here are 9 morning habits that gently power up your day — from the
            inside out.
          </p>
        </section>

        <section className="space-y-6 mb-8">
          <div>
            <h3 className="text-primary font-semibold">
              1. Wake Up Before You Need To
            </h3>
            <p>
              Not two hours early. Just a little before you have to.
            </p>
            <p>
              Give yourself the gift of slow time. Time that isn’t immediately
              claimed by messages, responsibilities, or noise.
            </p>
            <p>
              When you wake up early — even 15 minutes before your alarm — you
              start the day choosing yourself instead of reacting to the world.
            </p>
            <p>
              You’ll notice how your body feels less rushed, your thoughts less
              messy. You stop living on autopilot and start moving from
              awareness.
            </p>
            <p className="text-[#578d48]">
              Mornings are your foundation — don’t let the world build on it
              before you do.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              2. Don’t Touch Your Phone for the First 20 Minutes
            </h3>
            <p className="text-[#578d48]">
              Let me be honest: your phone is the fastest way to lose peace
              before the day begins.
            </p>
            <p>
              One notification, one headline, one text — and your energy is
              already scattered.
            </p>
            <p>
              You’re pulled into other people’s moods, stories, and expectations
              before you’ve even felt your own breath.
            </p>
            <p>
              So here’s what I started doing: I leave my phone in another room at
              night. When I wake up, I face my own thoughts first.
            </p>
            <p>
              I stretch. I breathe. I make my bed. I sip water.
            </p>
            <p>
              It’s uncomfortable at first — like your brain doesn’t know what to
              do with the silence. But soon, you’ll realize: that silence is
              what your soul has been craving.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              3. Hydrate Before You Caffeinate
            </h3>
            <p>
              Your brain and body wake up dehydrated. And when you immediately
              grab coffee, you’re just pouring stimulation on top of
              exhaustion.
            </p>
            <p>
              Try this: before your first sip of caffeine, drink a tall glass of
              water — slowly, intentionally. Feel it wake up your body from the
              inside.
            </p>
            <p>
              If you want to make it special, add lemon or a pinch of salt. Turn
              it into a ritual, not a chore.
            </p>
            <p className="text-[#578d48]">
              Water is simple. But it’s also sacred.
              <br />
              It’s your first act of care every single morning.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              4. Do One Thing That Grounds You
            </h3>
            <p>
              It could be journaling. It could be sitting in silence. It could
              be standing outside barefoot, eyes closed, breathing the morning
              air.
            </p>
            <p>
              Find one small thing that connects you to the present moment.
              <br />
              Something that pulls you out of your head and into your heart.
            </p>
            <p>
              I light a candle most mornings. I don’t pray in any structured way
              — I just breathe and say, “Help me move with calm today.”
            </p>
            <p className="text-[#578d48]">
              You don’t need a big ritual. You just need one grounding act that
              reminds you: peace is always available, you just have to pause
              long enough to feel it.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              5. Feed Your Mind Something Real
            </h3>
            <p>
              Instead of opening social media, read something that feeds your
              spirit — not your ego.
            </p>
            <p>
              A poem. A few lines from a book. A small piece of writing that
              helps you remember who you are beyond your job, your stress, your
              phone.
            </p>
            <p className="text-[#578d48]">
              We all become what we consume.
            </p>
            <p>
              When you start your morning with wisdom instead of noise, your
              mind learns to look inward instead of outward.
            </p>
            <p className="text-[#578d48]">
              That one choice changes the energy of your entire day.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              6. Move Gently — Not Aggressively
            </h3>
            <p>
              You don’t have to do a hard workout or run a marathon.
              <br />
              Sometimes your body doesn’t need intensity — it needs kindness.
            </p>
            <p>
              Try stretching. Try walking. Try yoga, or just slow, deep breaths
              that expand your chest.
            </p>
            <p className="text-[#578d48]">
              The goal isn’t to “burn calories.” It’s to wake up your body in
              gratitude.
            </p>
            <p className="text-[#578d48]">
              Movement tells your nervous system, “I’m safe. I’m alive.”
            </p>
            <p>
              It’s not about discipline. It’s about devotion — to your
              well-being.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              7. 📖 Write, Even If It’s Messy
            </h3>
            <p>
              You don’t have to be a writer to journal. You just have to be
              willing to be honest.
            </p>
            <p>
              Write down what’s swirling in your mind — worries, gratitude,
              random thoughts, anything.
              <br />
              You don’t need structure. You just need the truth.
            </p>
            <p className="text-[#578d48]">
              When you write, you take your thoughts out of your head and place
              them somewhere safe.
            </p>
            <p className="text-[#578d48]">
              It’s like clearing space in your mind before the world fills it
              again.
            </p>
            <p>
              If you’re not sure what to write, start with this question:
              <br />
              “What do I want to feel today?”
            </p>
            <p className="text-[#578d48]">
              That’s more powerful than any to-do list.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              8. Plan Your Day with Intention — Not Pressure
            </h3>
            <p className="text-[#578d48]">
              Here’s what I learned:
              <br />
              The way you plan your day shapes how you feel about it.
            </p>
            <p>
              Don’t fill your list with 20 things that guarantee failure.
              <br />
              Choose 3 meaningful things — the ones that actually move you
              forward or bring peace to your life.
            </p>
            <p>
              Then, leave room for life to happen.
            </p>
            <p>
              Structure is important, but so is softness.
            </p>
            <p className="text-[#578d48]">
              When you plan your day from love — not fear — you show up with
              more calm and focus.
            </p>
          </div>

          <div>
            <h3 className="text-primary font-semibold">
              9. Say Something Kind Before You Leave the House
            </h3>
            <p>
              Before you walk out the door — pause for 10 seconds.
            </p>
            <p>
              Look at yourself in the mirror and say something kind.
              <br />
              It could be simple: “You’re doing better than you think.”
              <br />
              Or, “Let’s make today gentle.”
            </p>
            <p className="text-[#578d48]">
              It might sound strange at first, but words carry energy.
            </p>
            <p className="text-[#578d48]">
              When you speak to yourself with warmth, you send that same warmth
              into the world.
            </p>
            <p className="text-[#578d48]">
              The tone of your day is set by the tone of your self-talk.
              <br />
              Start softly. Start kindly.
            </p>
          </div>
        </section>

        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            A Truth Most People Don’t Talk About
          </h2>
          <p className="text-[#578d48]">
            Morning habits aren’t about productivity.
            <br />
            They’re about presence.
          </p>
          <p>
            You don’t have to wake up early or meditate for an hour to change
            your life. You just have to stop letting the morning slip away
            unconsciously.
          </p>
          <p className="text-[#578d48]">
            The way you treat the first hour of your day is the way you treat
            your life.
          </p>
          <p>
            If you rush through it, you’ll rush through everything.
            <br />
            If you fill it with peace, you’ll find peace everywhere you go.
          </p>
          <p>
            And if you wake up tomorrow still tired, still struggling, still
            human — that’s okay too.
            <br />
            You don’t need perfect mornings to create a better life. You just
            need honest ones.
          </p>
        </section>

        <section className="space-y-4 mb-10">
          <h2 className="text-secondary text-2xl font-semibold">
            Final Thoughts
          </h2>
          <p className="text-[#578d48]">
            The truth is, life doesn’t slow down for anyone.
            <br />
            But you can choose to slow down within it.
          </p>
          <p>
            Wake up gently. Drink water. Step outside. Breathe. Move.
          </p>
          <p className="text-[#578d48]">
            Start your day with the same care you’d offer someone you love —
            because that’s exactly what you’re doing.
          </p>
          <p className="text-[#578d48]">
            You’re teaching your mind, body, and spirit that they deserve peace
            — not just on weekends, not just on vacations — but every single
            morning.
          </p>
          <p>
            You don’t have to fix your whole life before 10 a.m.
            <br />
            You just have to begin your day in a way that feels honest and kind.
          </p>
          <p className="text-[#578d48]">
            That’s what powers everything else.
          </p>
        </section>

        <HashtagSection tags={hashtags} />
        <ShareSection />
      </article>
    </>
  );
};

export default Blog12;
